import Dialog from "./shared/Dialog";
import {useEffect, useState} from "react";
import {getTemplates} from "../services/TemplatesService";
import {Box, Button, Checkbox, Collapse, Divider, FormControlLabel, MenuItem, Switch, Typography} from "@mui/material";
import {ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, FitnessCenter, CalendarMonth, Settings} from "@mui/icons-material";
import SelectCom from "./SelectCom";
import {experienceLevels} from "../enums/ExperienceLevels";
import {EquipmentTypes} from "../enums/EquipmentType";

const ChangeProgramDialog = ({isOpen, close, handleChangeProgram, trainee}) => {

  const [availableTemplates, setAvailableTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [includeRmWeek, setIncludeRmWeek] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedSettings, setAdvancedSettings] = useState({
    automatic: true,
    useProgressionAlgorithm: true,
    useRir: true
  });

  const [profileFields, setProfileFields] = useState({
    experienceLevel: trainee?.experienceLevel || "null",
    numOfWorkouts: trainee?.numOfDaysTraining || "null",
    bodyPart: "null",
    secondaryMuscle: "null",
    secondaryMuscle2: "null",
    equipmentType: trainee?.equipmentType || "GYM"
  })

  const updateProfileField = (value, keyName) => {
    setProfileFields(prv => {
      return {
        ...prv,
        [keyName] : value
      };
    })
  }

  useEffect(() => {
      getTemplates(0, 50,
          profileFields.numOfWorkouts,
          profileFields.experienceLevel,
          null,
          null,
          profileFields.equipmentType,
          true,
          null,
          null,
          trainee?.gender,
          trainee.companyId)
          .then(res => setAvailableTemplates(res.data.content))
  }, [profileFields, trainee?.gender])

  const SectionCard = ({children, title, icon}) => (
    <Box sx={{
      backgroundColor: "#F8FAFC",
      borderRadius: "12px",
      p: 2.5,
      mb: 2
    }}>
      {title && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          {icon}
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
            {title}
          </Typography>
        </Box>
      )}
      {children}
    </Box>
  );

  const SettingRow = ({label, checked, onChange, statusLabel}) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
      <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#1E293B" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Switch
          checked={checked}
          onChange={onChange}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#57837E' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#57837E' },
          }}
        />
        <Typography sx={{ fontSize: "11px", color: "#64748B", minWidth: "50px" }}>
          {statusLabel}
        </Typography>
      </Box>
    </Box>
  );

  return (
      <Dialog
        title={
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{
              fontSize: "22px",
              fontWeight: 600,
              color: "#57837E",
              mb: 0.5
            }}>
              הקצאת תוכנית
            </Typography>
            <Typography sx={{
              fontSize: "14px",
              color: "#64748B",
              fontWeight: 400
            }}>
              בחר תוכנית אימונים עבור המתאמן
            </Typography>
          </Box>
        }
        close={close}
        isOpen={isOpen}
        actions={
          <Box sx={{display: "flex", gap: 2, justifyContent: "center", width: "100%"}}>
            <Button
              onClick={close}
              variant="outlined"
              sx={{
                color: "#64748B",
                borderColor: "#E2E8F0",
                px: 4,
                "&:hover": {
                  borderColor: "#CBD5E1",
                  backgroundColor: "#F8FAFC"
                }
              }}
            >
              ביטול
            </Button>
            <Button
              disabled={!selectedTemplate || selectedTemplate === 'null'}
              onClick={() => handleChangeProgram(selectedTemplate, includeRmWeek, advancedSettings)}
              variant="contained"
              sx={{
                backgroundColor: "#57837E",
                px: 4,
                "&:hover": {
                  backgroundColor: "#456b67"
                },
                "&:disabled": {
                  backgroundColor: "#CBD5E1"
                }
              }}
            >
              עדכן לתוכנית חדשה
            </Button>
          </Box>
        }
      >
        <Box sx={{
          direction: "rtl",
          p: 2,
          display: "flex",
          gap: 3
        }}>
          {/* Left Column - Filters */}
          <Box sx={{ flex: 1 }}>
            <SectionCard title="סינון לפי פרופיל" icon={<FitnessCenter sx={{ fontSize: 18, color: "#57837E" }} />}>
              <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 1 }}>
                רמת מתאמן
              </Typography>
              <SelectCom
                id={"lvl"}
                width={"100%"}
                mainItem={"לא נבחר"}
                label={"רמת מתאמן"}
                contrast={"black"}
                height={"48px"}
                value={profileFields.experienceLevel}
                onChange={e => updateProfileField(e, "experienceLevel")}
                defaultValue={"null"}>
                {experienceLevels.map((res, i) => {
                  return <MenuItem key={i} value={res.value} sx={{display: "flex", alignItems: "center"}}>
                    <Typography sx={{marginLeft: "8px"}}>{res.name}</Typography>
                  </MenuItem>
                })}
              </SelectCom>

              <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 1, mt: 2 }}>
                מספר אימונים בשבוע
              </Typography>
              <SelectCom
                id={"numWorkouts"}
                width={"100%"}
                mainItem={"לא נבחר"}
                label={"מספר אימונים"}
                contrast={"black"}
                height={"48px"}
                value={profileFields.numOfWorkouts}
                onChange={e => updateProfileField(e, "numOfWorkouts")}
                defaultValue={"null"}>
                {Array.from(Array(7).keys()).map((_, i) => {
                  return <MenuItem key={i} value={i+1}>{i+1}</MenuItem>
                })}
              </SelectCom>

              <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 1, mt: 2 }}>
                ציוד קיים
              </Typography>
              <SelectCom
                id={"equipment"}
                width={"100%"}
                disabled
                mainItem={"לא נבחר"}
                label={"ציוד קיים"}
                contrast={"black"}
                height={"48px"}
                value={profileFields.equipmentType}
                onChange={e => updateProfileField(e, "equipmentType")}
                defaultValue={"null"}>
                {EquipmentTypes.map((res, i) => {
                  return <MenuItem key={i} value={res.value} sx={{display: "flex", alignItems: "center"}}>
                    <Typography sx={{marginLeft: "8px"}}>{res.name}</Typography>
                  </MenuItem>
                })}
              </SelectCom>
            </SectionCard>
          </Box>

          {/* Right Column - Template Selection & Settings */}
          <Box sx={{ flex: 1 }}>
            <SectionCard title="בחירת תוכנית" icon={<CalendarMonth sx={{ fontSize: 18, color: "#57837E" }} />}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeRmWeek}
                    onChange={(e) => setIncludeRmWeek(e.target.checked)}
                    sx={{
                      color: "#CBD5E1",
                      '&.Mui-checked': { color: '#57837E' },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px", color: "#1E293B" }}>
                    כלול שבוע RM (שבוע הכנה לתוכנית)
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 1 }}>
                תוכנית אימונים
              </Typography>
              <SelectCom
                id={"template"}
                width={"100%"}
                mainItem={"לא נבחר"}
                label={"פרופיל/תוכנית"}
                contrast={"black"}
                height={"auto"}
                value={selectedTemplate}
                onChange={e => setSelectedTemplate(e)}
                defaultValue={"null"}>
                {availableTemplates.map((value, i) => {
                  return <MenuItem
                    key={i}
                    value={value.id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      padding: "12px 16px",
                      borderBottom: "1px solid #f0f0f0",
                      "&:last-child": {
                        borderBottom: "none"
                      }
                    }}>
                    <Box sx={{ width: "100%" }}>
                      <Typography sx={{
                        fontWeight: 600,
                        fontSize: "14px",
                        marginBottom: "4px",
                        color: "#1E293B"
                      }}>
                        {value.nameHeb}
                      </Typography>

                      {value.descriptionHeb && (
                        <Typography sx={{
                          fontSize: "12px",
                          color: "#64748B",
                          marginBottom: "6px",
                          fontStyle: "italic"
                        }}>
                          {value.descriptionHeb}
                        </Typography>
                      )}

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "4px" }}>
                        <Typography sx={{ fontSize: "11px", color: "#57837E", fontWeight: "500" }}>
                          {value.numOfWorkouts} אימונים/שבוע
                        </Typography>

                        {value.numOfWeeks && (
                          <Typography sx={{ fontSize: "11px", color: "#57837E", fontWeight: "500" }}>
                            {value.numOfWeeks} שבועות
                          </Typography>
                        )}

                        <Typography sx={{ fontSize: "11px", color: "#57837E", fontWeight: "500" }}>
                          {value.gender === "FEMALE" ? "נקבה" : value.gender === "MALE" ? "זכר" : "אין מידע"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        <Typography sx={{ fontSize: "11px", color: "#64748B" }}>
                          {value.experienceLevel ? experienceLevels.find(lvl => lvl.value === value.experienceLevel)?.name : "אין מידע"}
                        </Typography>

                        {value.equipmentType && (
                          <Typography sx={{ fontSize: "11px", color: "#64748B" }}>
                            {EquipmentTypes.find(eq => eq.value === value.equipmentType)?.name || value.equipmentType}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                })}
              </SelectCom>
            </SectionCard>

            {/* Advanced Settings */}
            <Box
              onClick={() => setAdvancedOpen(prev => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                color: "#64748B",
                mb: 1,
                "&:hover": { color: "#1E293B" }
              }}
            >
              <Settings sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                הגדרות מתקדמות
              </Typography>
              {advancedOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </Box>

            <Collapse in={advancedOpen} timeout="auto">
              <Box sx={{
                p: 2,
                backgroundColor: "#F8FAFC",
                borderRadius: "12px"
              }}>
                <SettingRow
                  label="התקדמות אוטומטית"
                  checked={advancedSettings.automatic}
                  onChange={(e) => setAdvancedSettings(prev => ({...prev, automatic: e.target.checked}))}
                  statusLabel={advancedSettings.automatic ? "התקדמות אוטומטית" : "התקדמות ידנית"}
                />
                <Divider sx={{ my: 0.5 }} />

                <SettingRow
                  label="שימוש באלגוריתם התקדמות"
                  checked={advancedSettings.useProgressionAlgorithm}
                  onChange={(e) => setAdvancedSettings(prev => ({...prev, useProgressionAlgorithm: e.target.checked}))}
                  statusLabel={advancedSettings.useProgressionAlgorithm ? "מופעל" : "מבוטל"}
                />
                <Divider sx={{ my: 0.5 }} />

                <SettingRow
                  label="שימוש RIR"
                  checked={advancedSettings.useRir}
                  onChange={(e) => setAdvancedSettings(prev => ({...prev, useRir: e.target.checked}))}
                  statusLabel={advancedSettings.useRir ? "RIR פעיל" : "מבוטל"}
                />
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Dialog>
  );
};

export default ChangeProgramDialog;
